import React, { ChangeEvent, useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Box, CircularProgress } from "@mui/material";
import { Grid, InputAdornment } from "@mui/material";

import FullScreenDialog from "@/components/FullScreenDialog";
import GetImages from "@/components/GetImages";
import { InputField } from "@/components/InputField";
import { universalSearchStyles } from "@/views/Common/UniversalSearch/style";
import ChatListHeader from "@/views/Doctor/MobileChat/ChatListHeader";
import ChatList from "@/views/Doctor/MobileChat/ChatList";

import {
  setChatDialogOpen,
  setChatSearchOn,
  setSelectedChat,
  chat,
  searchChat,
  clearSearchChatList,
  setSearchList,
  fetchUser,
} from "@/store/slices/chat";
import { useAppDispatch, useAppSelector } from "@/store/hooks";

import { useFirebaseMessagingContext } from "@/context/Chat";
import useDebounce from "@/utils/hooks/useDebouncer";
import { storage } from "@/utils/Storage";
import { filterOutUndefined } from "@/utils";

import { ISearchChatRequest } from "@/models/ApiRequestResponse/chat";
import { ISessionKeys } from "@/models";
import { CONVERSATION_TYPE, ITabIndex } from "@/models/chat";
import { IRoleType } from "@/models/role";

const CHAT_SEARCH_LIMIT = 10;

const SearchChat: React.FC = () => {
  const [search, setSearch] = useState<string>("");
  const [offset, setOffset] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const {
    activeChatTab,
    searchChatList: searchList,
    isSearchLoading: isLoading,
  } = useAppSelector(chat);

  const { handleChannel, fetchConversationById } =
    useFirebaseMessagingContext();
  const debouncedSearch = useDebounce(search, 500);
  const dispatch = useAppDispatch();

  const sessionKeys = [
    ISessionKeys.USER_ID,
    ISessionKeys.ROLE_TYPE,
    ISessionKeys.DOCTOR_ID,
  ];
  const [currentUserId, roleType, doctorId] = sessionKeys.map((key) =>
    storage.getSessionToken(key)
  );

  const clearSearchHandler = () => {
    setSearch("");
    setOffset(0);
    setHasMore(true);
    dispatch(clearSearchChatList());
  };

  const closeSearchChatHandler = () => {
    dispatch(setChatSearchOn(false));
  };

  const onSelectChatHandler = (channelData: any) => {
    dispatch(
      fetchUser({
        doctor_ids: filterOutUndefined([channelData?.uniqueId]),
        appointment_ids: filterOutUndefined([channelData?.bookingId]),
        device_token_user_id: currentUserId!,
        user_ids: filterOutUndefined([channelData?.users?.patient_id]),
        ip_dash_id: filterOutUndefined([channelData?.hospitalPatientId]),
        member_ids: filterOutUndefined([channelData?.memberId]),
      })
    );
    handleChannel(channelData?.uniqueId);
    dispatch(
      setSelectedChat({
        ...channelData,
        isSearchedChat: true,
        ...{
          user_details: { name: channelData?.name },
        },
      })
    );
    dispatch(setChatSearchOn(false));
    dispatch(setChatDialogOpen(true));
    clearSearchHandler();
  };

  useEffect(() => {
    const performSearch = async () => {
      dispatch(clearSearchChatList());
      setOffset(0);
      setHasMore(true);

      const params: ISearchChatRequest = {
        search: debouncedSearch.trim(),
        type:
          activeChatTab === ITabIndex.APPOINTMENTS
            ? "APPOINTMENT"
            : "IN_PATIENT",
        limit: CHAT_SEARCH_LIMIT,
        offset: 0,
        doctor_id: roleType === IRoleType.DOCTOR ? currentUserId : doctorId,
      };

      const resultAction = await dispatch(searchChat(params));
      if (searchChat.fulfilled.match(resultAction)) {
        if (resultAction.payload.length < CHAT_SEARCH_LIMIT) {
          setHasMore(false);
        }
      }
    };

    if (debouncedSearch.trim() && currentUserId) {
      performSearch();
    } else {
      dispatch(clearSearchChatList());
      setOffset(0);
      setHasMore(true);
    }
  }, [debouncedSearch, currentUserId, dispatch, activeChatTab]);

  const handleLoadMore = async () => {
    if (isLoading || !hasMore || !debouncedSearch.trim()) {
      return;
    }

    const nextOffset = offset + CHAT_SEARCH_LIMIT;

    const params: ISearchChatRequest = {
      search: debouncedSearch.trim(),
      type:
        activeChatTab === ITabIndex.APPOINTMENTS
          ? CONVERSATION_TYPE.APPOINTMENT
          : CONVERSATION_TYPE.IN_PATIENT,
      limit: CHAT_SEARCH_LIMIT,
      offset: nextOffset,
      doctor_id: roleType === IRoleType.DOCTOR ? currentUserId : doctorId,
    };

    const resultAction = await dispatch(searchChat(params));
    setOffset(nextOffset);

    if (searchChat.fulfilled.match(resultAction)) {
      if (resultAction.payload.length < CHAT_SEARCH_LIMIT) {
        setHasMore(false);
      }
    }
  };

  useEffect(() => {
    if (!hasMore) {
      const isAppointmentType = activeChatTab === ITabIndex.APPOINTMENTS;
      (async () => {
        const searchListing = await Promise.all(
          searchList?.map(async (item) => {
            const chatDetails = await fetchConversationById(
              isAppointmentType ? item?.bookingId : item?.hospitalPatientId
            );
            return {
              ...item,
              ...chatDetails,
              ...(isAppointmentType
                ? { type: CONVERSATION_TYPE.APPOINTMENT }
                : { type: CONVERSATION_TYPE.IN_PATIENT }),
            };
          }) || []
        );
        dispatch(setSearchList(searchListing));
      })();
    }
  }, [hasMore]);
  return (
    <FullScreenDialog>
      <Grid item sx={universalSearchStyles.container}>
        <Box>
          <InputField
            fullWidth
            placeholder="Type here to search..."
            value={search}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setSearch(event.target.value)
            }
            InputProps={{
              startAdornment: (
                <InputAdornment
                  position="start"
                  onClick={closeSearchChatHandler}
                >
                  <GetImages name="LeftArrowIcon" width="20" height="18" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {search.length > 0 ? (
                    <CloseIcon
                      sx={universalSearchStyles.searchCloseMobile}
                      onClick={clearSearchHandler}
                    />
                  ) : (
                    <GetImages name="NavSearchIcon" width="24" height="24" />
                  )}
                </InputAdornment>
              ),
            }}
            autoFocus
          />
        </Box>
        <Grid item>
          <ChatListHeader chatList={searchList} />
          <Grid
            item
            xs={12}
            height={"100%"}
            sx={{ backgroundColor: "var(--neutral-100)" }}
          >
            {isLoading && searchList.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "50vh",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <ChatList
                onSelectChat={onSelectChatHandler}
                chatList={searchList}
                onLoadMore={handleLoadMore}
                isLoading={isLoading}
              />
            )}
          </Grid>
        </Grid>
      </Grid>
    </FullScreenDialog>
  );
};

export default SearchChat;
